/**
 * ComparativeReportingTab - Week-on-week, month-on-month comparisons
 * Visual comparison of sales performance across periods
 */
import { useState, useEffect } from 'react';
import { 
  TrendingUp, TrendingDown, Minus, Calendar, 
  RefreshCw, ArrowRight, DollarSign, ShoppingBag
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { api } from '@/utils/apiClient';

const TrendIcon = ({ direction }) => {
  if (direction === 'up') return <TrendingUp className="w-4 h-4 text-green-500" />;
  if (direction === 'down') return <TrendingDown className="w-4 h-4 text-red-500" />;
  return <Minus className="w-4 h-4 text-gray-500" />;
};

const ChangeDisplay = ({ data }) => {
  if (!data) return null;
  const color = data.direction === 'up' ? 'text-green-600 dark:text-green-400' : 
                data.direction === 'down' ? 'text-red-600 dark:text-red-400' : 
                'text-gray-600 dark:text-gray-400';
  return (
    <div className={`flex items-center gap-1 ${color}`}>
      <TrendIcon direction={data.direction} />
      <span className="font-bold">{data.change}%</span>
    </div>
  );
};

export default function ComparativeReportingTab() {
  const [weeklyData, setWeeklyData] = useState(null);
  const [monthlyData, setMonthlyData] = useState(null);
  const [dailyData, setDailyData] = useState(null);
  const [trends, setTrends] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('weekly');

  useEffect(() => {
    fetchAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    await Promise.all([
      fetchWeekly(),
      fetchMonthly(),
      fetchDaily(),
      fetchTrends()
    ]);
    setLoading(false);
  };

  const fetchWeekly = async () => {
    try {
      const res = await api.get('/admin/reports/compare/weekly');
      setWeeklyData(res.data);
    } catch (err) {
      console.error('Failed to fetch weekly:', err);
    }
  };

  const fetchMonthly = async () => {
    try {
      const res = await api.get('/admin/reports/compare/monthly');
      setMonthlyData(res.data);
    } catch (err) {
      console.error('Failed to fetch monthly:', err);
    }
  };

  const fetchDaily = async () => {
    try {
      const res = await api.get('/admin/reports/compare/daily');
      setDailyData(res.data);
    } catch (err) {
      console.error('Failed to fetch daily:', err);
    }
  };

  const fetchTrends = async () => {
    try {
      const res = await api.get('/admin/reports/trends', { params: { days: 30, group_by: 'day' } });
      setTrends(res.data);
    } catch (err) {
      console.error('Failed to fetch trends:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="comparative-reporting-tab">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
            <TrendingUp className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
          </div>
          <div>
            <h2 className="text-2xl font-heading text-foreground">Comparative Reports</h2>
            <p className="text-muted-foreground text-sm">Compare performance across periods</p>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={fetchAllData}
          className="rounded-full"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Tab Switcher */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {['daily', 'weekly', 'monthly'].map(tab => (
          <Button
            key={tab}
            variant={activeTab === tab ? 'default' : 'outline'}
            onClick={() => setActiveTab(tab)}
            className="rounded-full capitalize"
          >
            {tab}
          </Button>
        ))}
      </div>

      {/* Daily Comparison */}
      {activeTab === 'daily' && dailyData && (
        <div className="space-y-6">
          <div className="bg-card rounded-2xl border border-border p-6">
            <h3 className="font-heading text-lg text-foreground mb-4">Today's Performance</h3>
            <div className="grid md:grid-cols-3 gap-6">
              {/* Today */}
              <div className="bg-primary/5 rounded-xl p-4 border-2 border-primary">
                <div className="text-sm text-muted-foreground mb-2">Today</div>
                <div className="text-3xl font-bold text-foreground">
                  ${dailyData.today?.data?.total_sales?.toFixed(2) || '0.00'}
                </div>
                <div className="text-sm text-muted-foreground">
                  {dailyData.today?.data?.total_orders || 0} orders
                </div>
              </div>
              
              {/* vs Yesterday */}
              <div className="bg-muted/30 rounded-xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-muted-foreground">vs Yesterday</span>
                  <ChangeDisplay data={dailyData.vs_yesterday?.comparison?.sales} />
                </div>
                <div className="text-xl font-bold text-foreground">
                  ${dailyData.vs_yesterday?.data?.total_sales?.toFixed(2) || '0.00'}
                </div>
                <div className="text-sm text-muted-foreground">
                  {dailyData.vs_yesterday?.data?.total_orders || 0} orders
                </div>
              </div>
              
              {/* vs Same Day Last Week */}
              <div className="bg-muted/30 rounded-xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-muted-foreground">vs Last Week</span>
                  <ChangeDisplay data={dailyData.vs_last_week_same_day?.comparison?.sales} />
                </div>
                <div className="text-xl font-bold text-foreground">
                  ${dailyData.vs_last_week_same_day?.data?.total_sales?.toFixed(2) || '0.00'}
                </div>
                <div className="text-sm text-muted-foreground">
                  {dailyData.vs_last_week_same_day?.data?.total_orders || 0} orders
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Weekly Comparison */}
      {activeTab === 'weekly' && weeklyData && (
        <div className="space-y-6">
          <div className="bg-card rounded-2xl border border-border p-6">
            <h3 className="font-heading text-lg text-foreground mb-4">Week-on-Week Comparison</h3>
            
            {/* Period Cards */}
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div className="bg-primary/5 rounded-xl p-4 border-2 border-primary">
                <div className="text-sm text-muted-foreground mb-2">This Week</div>
                <div className="text-3xl font-bold text-foreground">
                  ${weeklyData.this_week?.data?.total_sales?.toFixed(2) || '0.00'}
                </div>
                <div className="text-sm text-muted-foreground">
                  {weeklyData.this_week?.data?.total_orders || 0} orders | 
                  Avg ${weeklyData.this_week?.data?.avg_order_value?.toFixed(2) || '0.00'}
                </div>
              </div>
              
              <div className="bg-muted/30 rounded-xl p-4">
                <div className="text-sm text-muted-foreground mb-2">Previous Week</div>
                <div className="text-3xl font-bold text-foreground">
                  ${weeklyData.previous_week?.data?.total_sales?.toFixed(2) || '0.00'}
                </div>
                <div className="text-sm text-muted-foreground">
                  {weeklyData.previous_week?.data?.total_orders || 0} orders |
                  Avg ${weeklyData.previous_week?.data?.avg_order_value?.toFixed(2) || '0.00'}
                </div>
              </div>
            </div>

            {/* Comparison Metrics */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-4 bg-muted/30 rounded-xl">
                <div className="text-sm text-muted-foreground mb-1">Sales</div>
                <ChangeDisplay data={weeklyData.comparison?.sales} />
              </div>
              <div className="text-center p-4 bg-muted/30 rounded-xl">
                <div className="text-sm text-muted-foreground mb-1">Orders</div>
                <ChangeDisplay data={weeklyData.comparison?.orders} />
              </div>
              <div className="text-center p-4 bg-muted/30 rounded-xl">
                <div className="text-sm text-muted-foreground mb-1">Avg Order</div>
                <ChangeDisplay data={weeklyData.comparison?.avg_order} />
              </div>
              <div className="text-center p-4 bg-muted/30 rounded-xl">
                <div className="text-sm text-muted-foreground mb-1">Items Sold</div>
                <ChangeDisplay data={weeklyData.comparison?.items} />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Monthly Comparison */}
      {activeTab === 'monthly' && monthlyData && (
        <div className="space-y-6">
          <div className="bg-card rounded-2xl border border-border p-6">
            <h3 className="font-heading text-lg text-foreground mb-4">Month-on-Month Comparison</h3>
            
            {/* Period Cards */}
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div className="bg-primary/5 rounded-xl p-4 border-2 border-primary">
                <div className="text-sm text-muted-foreground mb-2">This Month</div>
                <div className="text-3xl font-bold text-foreground">
                  ${monthlyData.this_month?.data?.total_sales?.toFixed(2) || '0.00'}
                </div>
                <div className="text-sm text-muted-foreground">
                  {monthlyData.this_month?.data?.total_orders || 0} orders
                </div>
              </div>
              
              <div className="bg-muted/30 rounded-xl p-4">
                <div className="text-sm text-muted-foreground mb-2">Previous Month</div>
                <div className="text-3xl font-bold text-foreground">
                  ${monthlyData.previous_month?.data?.total_sales?.toFixed(2) || '0.00'}
                </div>
                <div className="text-sm text-muted-foreground">
                  {monthlyData.previous_month?.data?.total_orders || 0} orders
                </div>
              </div>
            </div>

            {/* Comparison Metrics */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-4 bg-muted/30 rounded-xl">
                <div className="text-sm text-muted-foreground mb-1">Sales</div>
                <ChangeDisplay data={monthlyData.comparison?.sales} />
              </div>
              <div className="text-center p-4 bg-muted/30 rounded-xl">
                <div className="text-sm text-muted-foreground mb-1">Orders</div>
                <ChangeDisplay data={monthlyData.comparison?.orders} />
              </div>
              <div className="text-center p-4 bg-muted/30 rounded-xl">
                <div className="text-sm text-muted-foreground mb-1">Avg Order</div>
                <ChangeDisplay data={monthlyData.comparison?.avg_order} />
              </div>
              <div className="text-center p-4 bg-muted/30 rounded-xl">
                <div className="text-sm text-muted-foreground mb-1">Items</div>
                <ChangeDisplay data={monthlyData.comparison?.items} />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 30-Day Trend Chart */}
      {trends && trends.data && (
        <div className="bg-card rounded-2xl border border-border p-6">
          <h3 className="font-heading text-lg text-foreground mb-4">30-Day Sales Trend</h3>
          <div className="h-48 flex items-end justify-between gap-1">
            {trends.data.slice(-30).map((day, i) => {
              const maxSales = Math.max(...trends.data.map(d => d.sales));
              const height = maxSales > 0 ? (day.sales / maxSales) * 100 : 0;
              return (
                <div key={i} className="flex-1 flex flex-col items-center group">
                  <div 
                    className="w-full bg-primary/80 hover:bg-primary rounded-t transition-all cursor-pointer"
                    style={{ height: `${Math.max(2, height)}%` }}
                    title={`${day.period}: $${day.sales.toFixed(2)} (${day.orders} orders)`}
                  />
                </div>
              );
            })}
          </div>
          <div className="flex justify-between text-xs text-muted-foreground mt-2">
            <span>30 days ago</span>
            <span>Today</span>
          </div>
        </div>
      )}
    </div>
  );
}
